/* ==========================================================================
   WATCHCLUB.RO — PREMIUM WEB BUILDER INTERACTIVE ENGINE
   Split-Word Reveal • Staggered Blur-Reveal • Cursor Border Spotlight
   Magnetic Buttons • 3D Specular Tilt • Animated Counters • Live Clock
   ========================================================================== */
(() => {
  'use strict';

  const isFinePointer = window.matchMedia('(pointer: fine)').matches;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initChronoPremiumEngine() {
    // 1. Inject Top Scroll Progress Bar & Subtle Film Grain if not already present
    if (!document.querySelector('.chrono-scroll-progress')) {
      const prog = document.createElement('div');
      prog.className = 'chrono-scroll-progress';
      prog.setAttribute('aria-hidden', 'true');
      document.body.prepend(prog);
    }
    if (!document.querySelector('.chrono-grain')) {
      const grain = document.createElement('div');
      grain.className = 'chrono-grain';
      grain.setAttribute('aria-hidden', 'true');
      document.body.prepend(grain);
    }

    // Fallback scroll progress update for browsers without CSS animation-timeline
    if (!CSS.supports('animation-timeline: scroll()')) {
      const updateProgress = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        document.documentElement.style.setProperty('--scroll-progress', p.toFixed(4));
      };
      window.addEventListener('scroll', updateProgress, { passive: true });
      updateProgress();
    }

    // 2. Split-Word Hero Reveal ([data-split])
    document.querySelectorAll('[data-split]').forEach((el) => {
      if (el.dataset.splitDone === 'true') return;
      el.dataset.splitDone = 'true';
      let wordIdx = 0;
      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const frag = document.createDocumentFragment();
          node.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
            } else {
              const w = document.createElement('span');
              w.className = 'word';
              const inner = document.createElement('span');
              inner.style.setProperty('--i', wordIdx++);
              inner.textContent = part;
              w.appendChild(inner);
              frag.appendChild(w);
            }
          });
          return frag;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const clone = node.cloneNode(false);
          node.childNodes.forEach((c) => clone.appendChild(walk(c)));
          return clone;
        }
        return node.cloneNode(true);
      };
      const next = document.createDocumentFragment();
      Array.from(el.childNodes).forEach((c) => next.appendChild(walk(c)));
      el.textContent = '';
      el.appendChild(next);
    });

    requestAnimationFrame(() => {
      document.body.classList.add('chrono-ready');
    });

    // 3. Staggered Scroll Blur-Reveal (.reveal-up)
    const revealEls = document.querySelectorAll('.reveal-up');
    const byParent = new Map();
    revealEls.forEach((el) => {
      if (el.style.getPropertyValue('--d')) return;
      const p = el.parentElement;
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(el);
    });
    byParent.forEach((list) => {
      if (list.length > 1) {
        list.forEach((el, i) => el.style.setProperty('--d', `${Math.min(i * 65, 420)}ms`));
      }
    });

    if (!reducedMotion && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -35px 0px' }
      );
      revealEls.forEach((el) => io.observe(el));
    } else {
      revealEls.forEach((el) => el.classList.add('is-in'));
    }

    // 4. Cursor-Tracked Radial Border Spotlight (.bento-card, .chrono-spot-card)
    if (isFinePointer) {
      const bindSpotlights = () => {
        document.querySelectorAll('.bento-card, .chrono-spot-card').forEach((card) => {
          if (card.dataset.spotlightBound === 'true') return;
          card.dataset.spotlightBound = 'true';
          if (!card.querySelector('.chrono-card-glow')) {
            const glow = document.createElement('div');
            glow.className = 'chrono-card-glow';
            glow.setAttribute('aria-hidden', 'true');
            card.prepend(glow);
          }
          card.addEventListener('pointermove', (e) => {
            const r = card.getBoundingClientRect();
            card.style.setProperty('--mx', `${(e.clientX - r.left).toFixed(1)}px`);
            card.style.setProperty('--my', `${(e.clientY - r.top).toFixed(1)}px`);
          });
        });
      };
      bindSpotlights();
    }

    // 5. 3D Specular Tilt (.chrono-tilt) on Desktop
    if (isFinePointer && !reducedMotion) {
      document.querySelectorAll('.chrono-tilt').forEach((el) => {
        if (el.dataset.tiltBound === 'true') return;
        el.dataset.tiltBound = 'true';
        el.addEventListener('pointermove', (e) => {
          const r = el.getBoundingClientRect();
          const px = (e.clientX - r.left) / r.width;
          const py = (e.clientY - r.top) / r.height;
          const rx = (0.5 - py) * 6;
          const ry = (px - 0.5) * 7;
          el.style.transform = `perspective(1000px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) translateY(-4px)`;
          el.style.setProperty('--sx', `${(px * 100).toFixed(1)}%`);
          el.style.setProperty('--sy', `${(py * 100).toFixed(1)}%`);
        });
        el.addEventListener('pointerleave', () => {
          el.style.transform = '';
        });
      });
    }

    // 6. Magnetic Buttons (.magnetic) on Desktop
    if (isFinePointer && !reducedMotion) {
      document.querySelectorAll('.magnetic').forEach((el) => {
        if (el.dataset.magneticBound === 'true') return;
        el.dataset.magneticBound = 'true';
        el.addEventListener('pointermove', (e) => {
          const r = el.getBoundingClientRect();
          const dx = e.clientX - (r.left + r.width / 2);
          const dy = e.clientY - (r.top + r.height / 2);
          el.style.transform = `translate(${(dx * 0.18).toFixed(1)}px, ${(dy * 0.18).toFixed(1)}px)`;
        });
        el.addEventListener('pointerleave', () => {
          el.style.transform = '';
        });
      });
    }

    // 7. Animated Number Counters ([data-count])
    const countEls = document.querySelectorAll('[data-count]');
    if (countEls.length && 'IntersectionObserver' in window) {
      const countIO = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            countIO.unobserve(el);
            const target = parseFloat(el.dataset.count);
            const decimals = parseInt(el.dataset.decimals || '0', 10);
            const suffix = el.dataset.suffix || '';
            const prefix = el.dataset.prefix || '';
            const dur = 1400;
            const t0 = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - t0) / dur);
              const eased = 1 - Math.pow(1 - t, 3);
              el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          });
        },
        { threshold: 0.35 }
      );
      countEls.forEach((el) => countIO.observe(el));
    }

    // 8. Live Bucharest Clock in Hero Meta (#chronoLiveClock)
    const clockEl = document.getElementById('chronoLiveClock');
    if (clockEl) {
      const fmt = new Intl.DateTimeFormat('ro-RO', {
        timeZone: 'Europe/Bucharest',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      const updateClock = () => {
        clockEl.textContent = fmt.format(new Date()) + ' EET';
      };
      updateClock();
      setInterval(updateClock, 1000);
    }

    // 9. Interactive WebGL Domain-Warped fBM Gold-Obsidian Hero Shader (#chronoHeroShader)
    const canvas = document.getElementById('chronoHeroShader');
    if (canvas && !canvas.dataset.shaderInit) {
      canvas.dataset.shaderInit = 'true';
      const gl = canvas.getContext('webgl', { antialias: false, depth: false, stencil: false, powerPreference: 'low-power' });
      if (gl && !reducedMotion) {
        const vsSrc = 'attribute vec2 p; void main(){ gl_Position = vec4(p,0.0,1.0); }';
        const fsSrc = `
          precision mediump float;
          uniform vec2 u_res;
          uniform float u_time;
          uniform vec2 u_mouse;
          float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453); }
          float noise(vec2 p){
            vec2 i = floor(p), f = fract(p);
            vec2 u = f*f*(3.0-2.0*f);
            return mix(mix(hash(i), hash(i+vec2(1.0,0.0)), u.x),
                       mix(hash(i+vec2(0.0,1.0)), hash(i+vec2(1.0,1.0)), u.x), u.y);
          }
          float fbm(vec2 p){
            float v = 0.0, a = 0.5;
            for(int i=0;i<4;i++){ v += a * noise(p); p = p*2.03 + vec2(1.7,9.2); a *= 0.5; }
            return v;
          }
          void main(){
            vec2 uv = (gl_FragCoord.xy - 0.5 * u_res) / min(u_res.x, u_res.y);
            vec2 m = (u_mouse - 0.5) * 0.55;
            float t = u_time * 0.14;
            vec2 q = vec2(fbm(uv*1.5 + t), fbm(uv*1.5 - t + 3.1));
            vec2 r = vec2(fbm(uv*2.0 + 1.7*q + m + vec2(1.7,9.2) + 0.15*t),
                          fbm(uv*2.0 + 1.7*q - m + vec2(8.3,2.8) + 0.12*t));
            float f = fbm(uv*1.7 + 2.1*r);
            vec3 base = vec3(1.0, 1.0, 1.0);
            vec3 pearl = vec3(0.976, 0.976, 0.978);
            vec3 caramelLine = vec3(0.955, 0.925, 0.895);
            vec3 col = base;
            col = mix(col, pearl, clamp(f*f*1.1, 0.0, 1.0));
            col = mix(col, caramelLine, clamp(length(q)*0.07, 0.0, 1.0));
            float spot = exp(-4.0 * length(uv - m*0.7));
            col = mix(col, caramelLine, spot * 0.08);
            gl_FragColor = vec4(col, 1.0);
          }
        `;
        const compile = (type, src) => {
          const s = gl.createShader(type);
          gl.shaderSource(s, src);
          gl.compileShader(s);
          return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
        };
        const vs = compile(gl.VERTEX_SHADER, vsSrc);
        const fs = compile(gl.FRAGMENT_SHADER, fsSrc);
        if (vs && fs) {
          const prog = gl.createProgram();
          gl.attachShader(prog, vs);
          gl.attachShader(prog, fs);
          gl.linkProgram(prog);
          gl.useProgram(prog);
          const buf = gl.createBuffer();
          gl.bindBuffer(gl.ARRAY_BUFFER, buf);
          gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
          const loc = gl.getAttribLocation(prog, 'p');
          gl.enableVertexAttribArray(loc);
          gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
          const uRes = gl.getUniformLocation(prog, 'u_res');
          const uTime = gl.getUniformLocation(prog, 'u_time');
          const uMouse = gl.getUniformLocation(prog, 'u_mouse');
          let mx = 0.5, my = 0.5, tx = 0.5, ty = 0.5;
          window.addEventListener('pointermove', (e) => {
            tx = e.clientX / window.innerWidth;
            ty = 1.0 - e.clientY / window.innerHeight;
          }, { passive: true });
          const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 1.4);
            canvas.width = Math.floor(canvas.clientWidth * dpr);
            canvas.height = Math.floor(canvas.clientHeight * dpr);
            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.uniform2f(uRes, canvas.width, canvas.height);
          };
          resize();
          window.addEventListener('resize', resize, { passive: true });
          let visible = true;
          if ('IntersectionObserver' in window) {
            new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(canvas);
          }
          const start = performance.now();
          const loop = (now) => {
            if (visible && !document.hidden) {
              mx += (tx - mx) * 0.05;
              my += (ty - my) * 0.05;
              gl.uniform1f(uTime, (now - start) * 0.001);
              gl.uniform2f(uMouse, mx, my);
              gl.drawArrays(gl.TRIANGLES, 0, 3);
            }
            requestAnimationFrame(loop);
          };
          requestAnimationFrame(loop);
        }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initChronoPremiumEngine);
  } else {
    initChronoPremiumEngine();
  }
  document.addEventListener('shopify:section:load', initChronoPremiumEngine);
})();
